/**
 * Inventory Hooks
 *
 * Provides hooks for inventory CRUD operations with TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import { useCollectionSync } from './useEntitySync';
import { fromOffsetResponse, usePagedQuery } from './usePagedQuery';
import {
  toSupplierIntegrationView,
  type SetSupplierIntegrationBody,
  type SupplierIntegrationView,
} from '@/features/inventory/supplierIntegration';

// API types
export interface PartListItem {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  tags?: string[] | null;
  categoryId: string;
  categoryName: string | null;
  oemPartNumber: string | null;
  barcode: string | null;
  unit: string;
  costPriceCents: number;
  sellPriceCents: number;
  reorderPoint: number;
  maxStockLevel: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  totalQuantity: number;
  /** 90-day tenant usage when the list was fetched with sortBy=usage (#2672). */
  usageCount?: number;
}

export interface PartDetail extends PartListItem {
  markupPercent: number;
  reorderQuantity: number;
  safetyStock: number;
  stockLevels: StockLevelItem[];
  recentMovements: StockMovementItem[];
}

export interface StockLevelItem {
  id: string;
  partId?: string;
  binLocationId: string | null;
  binLocationCode: string | null;
  binLocationDescription: string | null;
  warehouseId: string;
  warehouseName: string;
  warehouseCode: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityOnOrder: number;
  expiryDate: string | null;
}

export interface ExpiringPart {
  partId: string;
  partName: string;
  sku: string;
  warehouseId: string;
  warehouseName: string;
  warehouseCode: string;
  quantity: number;
  expiryDate: string;
}

export interface StockMovementItem {
  warehouseId?: string;
  warehouseName?: string | null;
  binLocationId?: string | null;
  binLocationCode?: string | null;
  id: string;
  movementType: string;
  quantity: number;
  quantityBefore: number;
  quantityAfter: number;
  unitCostCents: number;
  reason: string | null;
  createdAt: string;
  performedByName: string;
}

export interface SupplierListItem {
  id: string;
  name: string;
  code: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  address: Record<string, string> | null;
  paymentTermsDays: number;
  defaultLeadTimeDays: number;
  isPreferred: boolean;
  isActive: boolean;
  notes: string | null;
  rating: number;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderListItem {
  id: string;
  orderNumber: string;
  supplierId: string;
  supplier: { id: string; name: string; deliveryDays: number };
  status: string;
  lineCount: number;
  totalQuantity: number;
  subtotalCents: number;
  taxCents: number;
  shippingCents: number;
  totalCents: number;
  notes: string | null;
  createdByName: string;
  orderDate: string;
  expectedDeliveryDate: string | null;
  actualDeliveryDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderDetail {
  id: string;
  orderNumber: string;
  supplierId: string;
  warehouseId: string;
  supplier: {
    id: string;
    name: string;
    code: string;
    deliveryDays: number;
    email: string | null;
    phone: string | null;
  };
  status: string;
  items: PurchaseOrderLineItem[];
  subtotalCents: number;
  taxCents: number;
  shippingCents: number;
  totalCents: number;
  notes: string | null;
  createdByName: string;
  approvedByName: string | null;
  orderDate: string;
  expectedDeliveryDate: string | null;
  actualDeliveryDate: string | null;
  approvedAt: string | null;
  // Supplier invoice matching ("laskutäsmäytys", #212).
  supplierInvoiceNumber: string | null;
  supplierInvoiceDate: string | null;
  supplierInvoiceTotalCents: number | null;
  invoiceMatched: boolean;
  invoiceDiscrepancyCents: number | null;
  invoiceMatchedAt: string | null;
  invoiceMatchedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderLineWorkOrder {
  bookingId: string;
  status: string | null;
  customerName: string | null;
  vehicleRegistration: string | null;
}

export interface PurchaseOrderLineItem {
  id: string;
  partId: string;
  partNumber: string;
  name: string;
  quantity: number;
  quantityReceived: number;
  unitCostCents: number;
  totalCostCents: number;
  /** Stock of the part in the order's warehouse right now; null when the order has no warehouse (#2859). */
  quantityOnHand?: number | null;
  /** Set when the line is tied to a specific work order (transit warehouse). */
  linkedBookingId: string | null;
  linkedWorkOrder: PurchaseOrderLineWorkOrder | null;
}

export interface ReceivePurchaseOrderLineResult {
  lineId: string;
  partId: string;
  quantityReceived: number;
  quantityOrdered: number;
  quantityReceivedTotal: number;
  destination: 'stock' | 'transit';
  lineComplete: boolean;
}

export interface ReceivePurchaseOrderResponse {
  success: boolean;
  fullyReceived: boolean;
  completedBookingIds: string[];
  lines: ReceivePurchaseOrderLineResult[];
}

export interface CategoryItem {
  id: string;
  name: string;
  parentCategoryId: string | null;
  description: string | null;
  sortOrder: number;
}

export interface WarehouseItem {
  id: string;
  name: string;
  code: string;
  address: unknown;
  isDefault: boolean;
  warehouseType: 'core' | 'transit';
  binLocations: {
    id: string;
    code: string;
    description: string | null;
    zone: string | null;
    aisle: string | null;
    rack: string | null;
    shelf: string | null;
  }[];
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface PartFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  categoryId?: string;
  supplierId?: string;
  isActive?: boolean;
  lowStock?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

export interface SupplierFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  isActive?: boolean;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

export interface PurchaseOrderFilters {
  [key: string]: string | number | undefined;
  q?: string;
  status?: string;
  supplierId?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

// Stats types
export interface InventoryStats {
  totalCount: number;
  lowStockCount: number;
  outOfStockCount: number;
  totalStockValueCents: number;
  topCategories: { category: string; count: number }[];
}

/**
 * Hook to fetch inventory statistics
 */
export function useInventoryStats() {
  return useQuery({
    queryKey: queryKeys.inventory.stats(),
    queryFn: () => api.get<InventoryStats>('/inventory/stats'),
    staleTime: 60 * 1000,
  });
}

// Parts hooks

export function useInventoryParts(
  filters: PartFilters = {},
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.inventory.parts.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<PartListItem>>('/inventory/parts', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 30 * 1000,
    enabled: options?.enabled,
  });
}

export interface InventoryPartsPageFilters {
  q?: string;
  categoryId?: string;
  supplierId?: string;
  isActive?: boolean;
  lowStock?: boolean;
  stockStatus?: string;
}

interface PartsOffsetResponse {
  data: PartListItem[];
  pagination: { total: number };
}

/** Page-number parts list (#2741). Always sends `limit` and `offset`, including 0. */
function partsFilterParams(
  filters: InventoryPartsPageFilters,
  limit: number,
  offset: number,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { limit, offset };
  if (filters.q !== undefined) params.q = filters.q;
  if (filters.categoryId !== undefined) params.categoryId = filters.categoryId;
  if (filters.supplierId !== undefined) params.supplierId = filters.supplierId;
  if (filters.isActive !== undefined) params.isActive = filters.isActive;
  if (filters.lowStock !== undefined) params.lowStock = filters.lowStock;
  if (filters.stockStatus !== undefined) params.stockStatus = filters.stockStatus;
  return params;
}

/** The route's page ceiling; the fewest requests for a full read. */
const ALL_PARTS_PAGE_SIZE = 200;

/**
 * Every part matching `filters`, read page by page (#2741).
 *
 * The parts list shows one page; restock orders, the PO part picker, export
 * and stock-take act on the whole matching set and call this instead of
 * reusing the rows on screen.
 */
export async function fetchAllInventoryParts(
  filters: InventoryPartsPageFilters = {},
): Promise<PartListItem[]> {
  const rows: PartListItem[] = [];
  for (let offset = 0; ; offset += ALL_PARTS_PAGE_SIZE) {
    const response = await api.get<PartsOffsetResponse>('/inventory/parts', {
      params: partsFilterParams(filters, ALL_PARTS_PAGE_SIZE, offset),
    });
    rows.push(...response.data);
    if (response.data.length === 0 || rows.length >= response.pagination.total) return rows;
  }
}

/** Query key for {@link fetchAllInventoryParts}; under `parts.lists()` so part mutations invalidate it. */
export function allInventoryPartsQueryKey(filters: InventoryPartsPageFilters = {}) {
  return [...queryKeys.inventory.parts.lists(), 'all', filters] as const;
}

export function useInventoryPartsPage(filters: InventoryPartsPageFilters = {}) {
  return usePagedQuery<PartListItem, InventoryPartsPageFilters>({
    queryKey: queryKeys.inventory.parts.lists(),
    filters,
    defaultSort: { sortBy: 'quantity', sortDir: 'asc' },
    staleTime: 30_000,
    fetchPage: async ({ filters: pageFilters, limit, offset, sortBy, sortDir }) => {
      const params = partsFilterParams(pageFilters, limit, offset);
      if (sortBy !== undefined) params.sortBy = sortBy;
      if (sortDir !== undefined) params.sortDir = sortDir;
      const response = await api.get<PartsOffsetResponse>('/inventory/parts', { params });
      return fromOffsetResponse(response);
    },
  });
}

export function useInventoryPart(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inventory.parts.detail(id ?? ''),
    queryFn: () => api.get<PartDetail>(`/inventory/parts/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

/** Response from POST /inventory/parts — only the fields callers rely on (#724). */
export interface CreatePartResponse {
  id: string;
}

export function useCreatePart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      sku: string;
      name: string;
      description?: string;
      tags?: string[];
      categoryId: string;
      oemPartNumber?: string;
      barcode?: string;
      costPriceCents: number;
      sellPriceCents: number;
      reorderPoint?: number;
      reorderQuantity?: number;
      maxStockLevel?: number;
      unit?: string;
      isActive?: boolean;
    }) => api.post<CreatePartResponse>('/inventory/parts', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.parts.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stats() });
    },
  });
}

export function useUpdatePart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/inventory/parts/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.parts.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.parts.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stats() });
    },
  });
}

export function useDeletePart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/inventory/parts/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.parts.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stats() });
    },
  });
}

/** Result of a bulk parts import (POST /inventory/parts/import). #227 */
export interface ImportResult {
  created: number;
  updated: number;
  errors: Array<{ row: number; field?: string; message: string }>;
}

/**
 * Bulk-import parts from parsed CSV/JSON rows. Each row is upserted by SKU on
 * the backend; per-row validation/DB failures come back in `errors`. #227
 */
export function useImportParts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (items: Array<Record<string, unknown>>) =>
      api.post<ImportResult>('/inventory/parts/import', { items }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    },
  });
}

// Stock hooks

export function useStockLevels(enabled = true) {
  return useQuery({
    queryKey: queryKeys.inventory.stockLevels(),
    enabled,
    queryFn: () => api.get<{ data: StockLevelItem[] }>('/inventory/stock-levels'),
    staleTime: 30 * 1000,
  });
}

export function useStockAdjustment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      partId: string;
      warehouseId: string;
      binLocationId?: string | null;
      quantity: number;
      movementType: string;
      notes?: string;
      referenceId?: string;
      /** YYYY-MM-DD; explicit null clears the expiry on the row (#209) */
      expiryDate?: string | null;
    }) => api.post('/inventory/stock-adjustments', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.parts.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stockLevels() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stats() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.inventory.all,
        predicate: (query) => query.queryKey[1] === 'expiring',
      });
    },
  });
}

/**
 * Hook to fetch parts whose stock_levels rows expire within `days` (default 30).
 * Already-expired rows (negative days remaining) are also included so they
 * can be cleared. Issue #209.
 */
export function useExpiringParts(days = 30, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.inventory.expiring(days),
    queryFn: () =>
      api.get<{ items: ExpiringPart[] }>('/inventory/expiring', {
        params: { days: String(days) },
      }),
    select: (data) => data.items,
    staleTime: 60 * 1000,
    enabled: options.enabled ?? true,
  });
}

// Supplier hooks

export function useSuppliers(filters: SupplierFilters = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.inventory.suppliers.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<SupplierListItem>>('/inventory/suppliers', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 60 * 1000,
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      code: string;
      contactName?: string;
      email?: string;
      phone?: string;
      paymentTermsDays?: number;
      defaultLeadTimeDays?: number;
    }) => api.post('/inventory/suppliers', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.suppliers.lists() });
    },
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/inventory/suppliers/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.suppliers.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.suppliers.lists() });
    },
  });
}

/** Cache key for GET/PUT /supplier-catalog/:id/integration. The stored secret is never part of this cache. */
export function supplierIntegrationQueryKey(supplierId: string) {
  return [...queryKeys.inventory.suppliers.all, 'integration', supplierId] as const;
}

export function useSupplierIntegration(supplierId: string | undefined) {
  return useQuery({
    queryKey: supplierIntegrationQueryKey(supplierId ?? ''),
    queryFn: async (): Promise<SupplierIntegrationView> => {
      if (!supplierId) throw new Error('Missing supplier id');
      const raw = await api.get<unknown>(`/supplier-catalog/${supplierId}/integration`);
      const view = toSupplierIntegrationView(raw);
      if (!view) throw new Error('Invalid supplier integration response');
      return view;
    },
    enabled: Boolean(supplierId),
    staleTime: 30 * 1000,
  });
}

export function useUpdateSupplierIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ supplierId, body }: { supplierId: string; body: SetSupplierIntegrationBody }) =>
      api.put<unknown>(`/supplier-catalog/${supplierId}/integration`, body),
    onSuccess: (raw, { supplierId }) => {
      const view = toSupplierIntegrationView(raw);
      if (view) queryClient.setQueryData(supplierIntegrationQueryKey(supplierId), view);
      void queryClient.invalidateQueries({ queryKey: supplierIntegrationQueryKey(supplierId) });
    },
  });
}

// Purchase Order hooks

export function usePurchaseOrders(filters: PurchaseOrderFilters = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.inventory.purchaseOrders.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<PurchaseOrderListItem>>('/inventory/purchase-orders', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 30 * 1000,
  });
}

export function usePurchaseOrder(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inventory.purchaseOrders.detail(id ?? ''),
    queryFn: () => api.get<PurchaseOrderDetail>(`/inventory/purchase-orders/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export interface CreatePurchaseOrderResponse {
  id: string;
  orderNumber: string;
  status: string;
  totalCents: number;
  createdAt: string;
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      supplierId: string;
      warehouseId: string;
      expectedDeliveryDate?: string;
      notes?: string;
      shippingCents?: number;
      lines: {
        partId: string;
        quantityOrdered: number;
        unitPriceCents: number;
        notes?: string;
        /** Ties a PO line back to the work order it was ordered for (#725). */
        linkedBookingId?: string;
      }[];
    }) => api.post<CreatePurchaseOrderResponse>('/inventory/purchase-orders', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.partsDashboard() });
    },
  });
}

export interface UpdatePurchaseOrderResponse {
  id: string;
  orderNumber: string;
  status: string;
  totalCents: number;
  updatedAt: string;
}

/** Edit a draft purchase order's supplier/warehouse/dates/notes and, optionally, replace its full line list (#726). */
export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        supplierId?: string;
        warehouseId?: string;
        expectedDeliveryDate?: string | null;
        notes?: string | null;
        shippingCents?: number;
        lines?: {
          partId: string;
          quantityOrdered: number;
          unitPriceCents: number;
          notes?: string;
          linkedBookingId?: string;
        }[];
      };
    }) => api.patch<UpdatePurchaseOrderResponse>(`/inventory/purchase-orders/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.partsDashboard() });
    },
  });
}

export function useUpdatePurchaseOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/inventory/purchase-orders/${id}/status`, { status }),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.lists() });
    },
  });
}

export function useReceivePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      lines,
    }: {
      id: string;
      lines: { lineId: string; quantityReceived: number }[];
    }) => api.post<ReceivePurchaseOrderResponse>(`/inventory/purchase-orders/${id}/receive`, { lines }),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.parts.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stockLevels() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stats() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.transitWarehouse() });
    },
  });
}

/** Match a fully-received PO against its supplier invoice ("laskutäsmäytys", #212). */
export function useMatchPurchaseOrderInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { invoiceNumber: string; invoiceDate: string; invoiceTotalCents: number };
    }) =>
      api.post<{ success: boolean; discrepancyCents: number; isExactMatch: boolean }>(
        `/inventory/purchase-orders/${id}/match-invoice`,
        data,
      ),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.lists() });
    },
  });
}

// RMA / returns to supplier (#229)

export interface ReturnToSupplierLine {
  id: string;
  partId: string;
  partName: string;
  quantity: number;
  unitCostCents: number;
  reason: string;
}

export type ReturnStatus = 'created' | 'shipped' | 'received' | 'refunded' | 'credited';

export interface ReturnToSupplier {
  id: string;
  purchaseOrderId: string;
  supplierId: string;
  supplierName: string;
  status: ReturnStatus;
  reason: string;
  totalCents: number;
  createdAt: string;
  lines: ReturnToSupplierLine[];
}

export interface CreateReturnInput {
  purchaseOrderId: string;
  lines: { purchaseOrderLineId: string; quantity: number; reason: string }[];
  shippingCostCents?: number;
  trackingNumber?: string;
}

export function useReturns(status?: string) {
  return useQuery({
    queryKey: queryKeys.inventory.returns(status),
    queryFn: () =>
      api.get<{ returns: ReturnToSupplier[] }>('/inventory/returns', {
        params: status ? { status } : {},
      }),
    select: (data) => data.returns,
    staleTime: 30 * 1000,
  });
}

export function useCreateReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReturnInput) => api.post<ReturnToSupplier>('/inventory/returns', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    },
  });
}

export function useUpdateReturnStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/inventory/returns/${id}/status`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.returns() });
    },
  });
}

// Warranty claims (#994, split from #229 osa 3)

export type WarrantyClaimType = 'defective' | 'premature_failure' | 'wrong_part';
export type WarrantyClaimStatus = 'open' | 'submitted' | 'approved' | 'rejected' | 'refunded';

export interface WarrantyClaim {
  id: string;
  partId: string;
  partName: string;
  supplierId: string;
  supplierName: string;
  purchaseOrderId: string | null;
  workOrderId: string | null;
  claimType: WarrantyClaimType;
  description: string | null;
  quantity: number;
  status: WarrantyClaimStatus;
  claimAmountCents: number | null;
  refundAmountCents: number | null;
  createdAt: string;
}

export interface CreateWarrantyClaimInput {
  partId: string;
  supplierId: string;
  purchaseOrderId?: string;
  workOrderId?: string;
  claimType: WarrantyClaimType;
  description?: string;
  quantity: number;
  claimAmountCents?: number;
}

export function useWarrantyClaims(status?: string) {
  return useQuery({
    queryKey: queryKeys.inventory.warrantyClaims(status),
    queryFn: () =>
      api.get<{ claims: WarrantyClaim[] }>('/inventory/warranty-claims', {
        params: status ? { status } : {},
      }),
    select: (data) => data.claims,
    staleTime: 30 * 1000,
  });
}

export function useCreateWarrantyClaim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWarrantyClaimInput) => api.post<WarrantyClaim>('/inventory/warranty-claims', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.warrantyClaims() });
    },
  });
}

export function useUpdateWarrantyClaimStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, refundAmountCents }: { id: string; status: string; refundAmountCents?: number }) =>
      api.patch(`/inventory/warranty-claims/${id}`, { status, refundAmountCents }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.warrantyClaims() });
    },
  });
}

// Lookup hooks

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.inventory.categories(),
    queryFn: () => api.get<{ data: CategoryItem[] }>('/inventory/categories'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useWarehouses() {
  return useQuery({
    queryKey: queryKeys.inventory.warehouses(),
    queryFn: () => api.get<{ data: WarehouseItem[] }>('/inventory/warehouses'),
    staleTime: 5 * 60 * 1000,
  });
}

// Bin locations (#1166) — CRUD within a warehouse. The list itself comes from
// useWarehouses (each warehouse carries its binLocations); these mutations just
// invalidate that query on success.

export interface CreateBinLocationInput {
  warehouseId: string;
  code: string;
  description?: string | null;
  zone?: string | null;
  aisle?: string | null;
  rack?: string | null;
  shelf?: string | null;
}

export type UpdateBinLocationInput = Partial<Omit<CreateBinLocationInput, 'warehouseId'>>;

export function useCreateBinLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBinLocationInput) => api.post('/inventory/bin-locations', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.warehouses() });
    },
  });
}

export function useUpdateBinLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBinLocationInput }) =>
      api.patch(`/inventory/bin-locations/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.warehouses() });
    },
  });
}

export function useDeleteBinLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/inventory/bin-locations/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.warehouses() });
    },
  });
}

// Transit Warehouse types

export interface TransitWarehousePartItem {
  partId: string;
  partName: string;
  partSku: string;
  quantityOrdered: number;
  quantityReceived: number;
  quantityConsumed: number;
  quantityRemaining: number;
  poOrderNumber: string;
  consumptionStatus: string;
  daysSinceReceived: number | null;
  workOrderPartId: string | null;
  unitPriceCents: number;
  resolutionNote: string | null;
}

export interface TransitWarehouseBooking {
  bookingId: string;
  customerName: string;
  vehicleInfo: string;
  bookingStatus: string;
  parts: TransitWarehousePartItem[];
  totalValueCents: number;
  oldestReceiptDays: number | null;
}

export interface TransitWarehouseResponse {
  data: TransitWarehouseBooking[];
  summary: {
    totalBookings: number;
    totalParts: number;
    totalValueCents: number;
    staleCount: number;
  };
}

export interface TransitWarehouseFilters {
  [key: string]: string | number | boolean | undefined;
  filter?: 'all' | 'unresolved' | 'stale';
  q?: string;
}

// Transit Warehouse hooks

export function useTransitWarehouse(filters: TransitWarehouseFilters = {}) {
  return useQuery({
    queryKey: queryKeys.inventory.transitWarehouse(filters),
    queryFn: () => api.get<TransitWarehouseResponse>('/inventory/transit-warehouse', { params: filters }),
    staleTime: 30 * 1000,
  });
}

export function useConfirmPartsConsumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      parts,
    }: {
      workOrderId: string;
      parts: { workOrderPartId: string; quantityConsumed: number; action: 'consumed' | 'leftover' }[];
    }) => api.post(`/work-orders/${workOrderId}/confirm-parts-consumption`, { parts }),
    onSuccess: (_, { workOrderId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.transitWarehouse() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stockLevels() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stats() });
      // Consumed quantities land on the invoice preview for this work order (#2643).
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoicePreview(workOrderId) });
      // Billing lines read consumed parts via useWorkOrderBillingLines (`['workOrderParts', id]`).
      void queryClient.invalidateQueries({ queryKey: ['workOrderParts', workOrderId] });
    },
    // The dialog offers parts-status `consumableParts`. Re-read it on success
    // (confirmed rows drop out) and on a refusal (the list was stale) (#3270).
    onSettled: (_data, _error, { workOrderId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.partsStatus(workOrderId) });
    },
  });
}

export function useResolveLeftover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      items: {
        workOrderPartId: string;
        partId: string;
        quantity: number;
        action: 'keep' | 'return_to_supplier';
        warehouseId?: string;
        binLocationId?: string;
      }[];
    }) => api.post<{ success: boolean; returnNumber: string | null }>('/inventory/transit-warehouse/resolve-leftover', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.transitWarehouse() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stockLevels() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stats() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.returns() });
    },
  });
}

/** Attach or clear a foreman note on a still-unresolved leftover (#728). */
export function useSetLeftoverNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderPartId, note }: { workOrderPartId: string; note: string | null }) =>
      api.patch<{ success: boolean; note: string | null }>(
        `/inventory/transit-warehouse/parts/${workOrderPartId}/note`,
        { note },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.transitWarehouse() });
    },
  });
}

// Parts Dashboard types

export type PartNeedStatus = 'needed' | 'reserved' | 'ordered' | 'backordered' | 'arrived' | 'installed' | 'dismissed';

export interface PartsDashboardItem {
  workOrderNumber?: string | null;
  id: string;
  partName: string;
  partSku: string;
  partId: string | null;
  quantity: number;
  unitPriceCents: number;
  bookingId: string;
  bookingStatus: string;
  customerName: string;
  vehicleRegistration: string;
  vehicleMakeModel: string;
  mechanicId: string | null;
  mechanicName: string | null;
  scheduledAt: string;
  /** Total on-hand across warehouses; null when the need has no catalog part match. */
  currentStock: number | null;
  status: PartNeedStatus;
  /** Hand-set order status; null means still automatic. Omitted by older servers (#3567). */
  orderStatus?: 'ordered' | 'arrived' | 'backordered' | null;
  /** When the hand-set order status was stored (#3567). */
  orderStatusSetAt?: string | null;
  /** PO-derived status, sent when it differs from a hand-set status (#3567). */
  purchaseOrderStatus?: 'ordered' | 'arrived' | 'backordered' | null;
  dismissedReason: string | null;
  reservationInfo: {
    reservationId: string;
    quantity: number;
    status: string;
  } | null;
  poInfo: {
    poId: string;
    poOrderNumber: string;
    poStatus: string;
    supplierId: string;
    supplierName: string;
    expectedDeliveryDate: string | null;
    quantityOrdered: number;
    quantityReceived: number;
  } | null;
  workOrderPartInfo: {
    workOrderPartId: string;
    consumptionStatus: string;
    quantity: number;
  } | null;
}

export interface PartsDashboardSummary {
  totalNeeded: number;
  totalReserved: number;
  totalOrdered: number;
  /** Omitted by servers that predate the backordered status (#3567). */
  totalBackordered?: number;
  totalArrived: number;
  totalInstalled: number;
  totalDismissed: number;
  totalValueCents: number;
}

export interface PartsDashboardResponse {
  items: PartsDashboardItem[];
  summary: PartsDashboardSummary;
}

export interface PartsDashboardFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  status?: PartNeedStatus | 'all';
  bookingStatus?: 'active' | 'all';
  mechanicId?: string;
  supplierId?: string;
  availability?: 'available' | 'unavailable' | 'all';
  dateFrom?: string;
  dateTo?: string;
}

// Parts Dashboard hooks

export function usePartsDashboard(filters: PartsDashboardFilters = {}) {
  return useQuery({
    queryKey: queryKeys.inventory.partsDashboard(filters),
    queryFn: () => api.get<PartsDashboardResponse>('/inventory/parts-needed', { params: filters }),
    staleTime: 30 * 1000,
  });
}

export function useDismissPartNeed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/inventory/parts-needed/${id}/dismiss`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.partsDashboard() });
    },
  });
}

export function useRestorePartNeed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post(`/inventory/parts-needed/${id}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.partsDashboard() });
    },
  });
}

/** Reserve a part from stock for a work order (#725) — wraps the existing stock_reservations lifecycle. */
export function useReservePartForWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId, partNumber, quantity }: { bookingId: string; partNumber: string; quantity: number }) =>
      api.post<{ reservationId: string; warehouseId: string; partId: string; quantity: number }>(
        `/work-orders/${bookingId}/part-reservations`,
        { partNumber, quantity },
      ),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.partsDashboard() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stockLevels() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.partsStatus(bookingId) });
    },
  });
}

/** Release a reservation created via useReservePartForWorkOrder, without consuming stock (#725). */
export function useReleasePartReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId, reservationId }: { bookingId: string; reservationId: string }) =>
      api.post(`/work-orders/${bookingId}/part-reservations/${reservationId}/release`),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.partsDashboard() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stockLevels() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.partsStatus(bookingId) });
    },
  });
}

/** Consume a reservation (the part is installed) — decrements on-hand stock and closes the hold (#727). */
export function useConsumePartReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId, reservationId }: { bookingId: string; reservationId: string }) =>
      api.post<{ reservationId: string; partNumber: string; partName: string; quantity: number; status: string }>(
        `/work-orders/${bookingId}/part-reservations/${reservationId}/consume`,
      ),
    onSuccess: (_, { bookingId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.partsDashboard() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.stockLevels() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.partsStatus(bookingId) });
      // Installed reservation stock becomes a billed part on this work order (#2643).
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoicePreview(bookingId) });
      // Billing lines read consumed parts via useWorkOrderBillingLines (`['workOrderParts', id]`).
      void queryClient.invalidateQueries({ queryKey: ['workOrderParts', bookingId] });
    },
  });
}

// Part bundles (#3350 phase 1) — named, parts-only lists inserted into a service in one selection.

export interface PartBundleListItem {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  itemCount: number;
  /** Items whose catalog part has been deactivated since. */
  unavailableCount: number;
}

export interface PartBundleItem {
  id: string;
  partId: string;
  sku: string;
  name: string;
  unit: string;
  categoryId: string;
  isActive: boolean;
  quantity: number;
  sortOrder: number;
  /** Current catalog sell price, VAT-inclusive (the same figure `PartListItem.sellPriceCents` carries). */
  sellPriceCents: number;
}

export interface PartBundleDetail {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  items: PartBundleItem[];
}

export interface PartBundleInput {
  name: string;
  description?: string | null;
  items: { partId: string; quantity: number }[];
}

/** One service-part line a bundle expands to, priced from today's catalog. */
export interface PartBundleExpansionLine {
  partId: string;
  name: string;
  categoryId: string;
  quantity: number;
  /** Gross (VAT-inclusive) unit price, converted from the catalog net once at `vatPercent`. */
  unitPriceGrossCents: number;
}

export interface PartBundleExpansion {
  bundleId: string;
  bundleName: string;
  /** The rate `unitPriceGrossCents` was computed at; a line must carry it so its net round-trips. */
  vatPercent: number;
  lines: PartBundleExpansionLine[];
}

export interface PartBundleFilters {
  q?: string;
}

interface PartBundlesOffsetResponse {
  data: PartBundleListItem[];
  pagination: { total: number };
}

/** Bundle caches live under one prefix so every bundle mutation can invalidate them together. */
export const partBundlesQueryKey = [...queryKeys.inventory.all, 'partBundles'] as const;

function partBundleParams(filters: PartBundleFilters, limit: number, offset: number) {
  const params: Record<string, string | number> = { limit, offset };
  if (filters.q) params.q = filters.q;
  return params;
}

const PART_BUNDLE_SYNC = {
  relevantScopes: ['details'],
  invalidationKeys: [{ queryKey: partBundlesQueryKey }],
};

/**
 * Live bundle changes from other tabs and colleagues: the server announces every
 * create, update and delete as a `part_bundle` entity event, and the bundle caches
 * are refetched so a renamed or deleted bundle is never offered from a stale list.
 */
export function usePartBundleSync(): void {
  useCollectionSync('part_bundle', PART_BUNDLE_SYNC);
}

/** Page-number bundle list for the Inventory tab. */
export function usePartBundlesPage(filters: PartBundleFilters = {}) {
  usePartBundleSync();
  return usePagedQuery<PartBundleListItem, PartBundleFilters>({
    queryKey: [...partBundlesQueryKey, 'list'],
    filters,
    staleTime: 30_000,
    fetchPage: async ({ filters: pageFilters, limit, offset }) =>
      fromOffsetResponse(
        await api.get<PartBundlesOffsetResponse>('/inventory/part-bundles', {
          params: partBundleParams(pageFilters, limit, offset),
        }),
      ),
  });
}

/** First page (at most 50) of bundles for the work-order picker. */
export function usePartBundleOptions(filters: PartBundleFilters = {}, options: { enabled?: boolean } = {}) {
  usePartBundleSync();
  return useQuery({
    queryKey: [...partBundlesQueryKey, 'options', filters],
    queryFn: () =>
      api.get<PartBundlesOffsetResponse>('/inventory/part-bundles', {
        params: partBundleParams(filters, 50, 0),
      }),
    staleTime: 15_000,
    enabled: options.enabled ?? true,
  });
}

export function usePartBundle(id: string | undefined) {
  return useQuery({
    queryKey: [...partBundlesQueryKey, 'detail', id ?? ''],
    queryFn: () => api.get<PartBundleDetail>(`/inventory/part-bundles/${id}`),
    enabled: !!id,
    // A bundle's prices follow the catalog; never edit from a cached copy.
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Expands a bundle at today's catalog prices. Always a fresh request (never cached):
 * a price or availability change must reach the very next insert. Read-only.
 */
export function fetchPartBundleExpansion(id: string): Promise<PartBundleExpansion> {
  return api.get<PartBundleExpansion>(`/inventory/part-bundles/${id}/expansion`);
}

export function useCreatePartBundle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PartBundleInput) => api.post<PartBundleDetail>('/inventory/part-bundles', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partBundlesQueryKey });
    },
  });
}

export function useUpdatePartBundle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PartBundleInput> }) =>
      api.patch<PartBundleDetail>(`/inventory/part-bundles/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partBundlesQueryKey });
    },
  });
}

export function useDeletePartBundle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/inventory/part-bundles/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partBundlesQueryKey });
    },
  });
}
